/**
 * „Entwickler im Chat“ — der Dreier-Modus. (15.10.2026)
 *
 * Martin: „dass du (die Entwickler-Claude-Sitzung auf dem Server) dich dazuschalten kannst, wenn ich mit Vespera kommuniziere — als
 * dritte Person — und wir miteinander reden können, bis das Problem gelöst ist.“
 *
 * EIN Besitzer für die Regeln, die Server (Route, Faden), Einstellungsseite und Chat gleich rechnen müssen:
 *   - nur Konto 1 (der Betreiber, wie Warte-Satz und Morse); Vorgabe AUS — nur ein ausdrückliches `true` schaltet ein;
 *   - ein Beitrag hat höchstens BEITRAG_HOECHSTENS Zeichen, höchstens BEITRAEGE_JE_STUNDE je Stunde;
 *   - gespeichert wird er als `role: "system"` mit `metadata.source = "entwickler"` — NIE als user (Martins Wort) und nie als
 *     assistant (Vesperas Wort). Jeder Leser, der nur user/assistant nimmt (Gedächtnis, Biografie, Kanal-Verlauf, Telegram),
 *     übersieht ihn darum von selbst; die, die ihn zeigen (Chat-Blase, Faden an der Nachricht), erkennen ihn an `istEntwicklerZeile`.
 *
 * ⚠️ Ein Beitrag ist NIE ein Ja und NIE Martins Freigabe: er beginnt keinen Zug (server/injektions-schutz.ts kennt ihn nicht als
 *    Nachricht). Martins erster Zug nach ihm galt bis 16.10.2026 als fremd; seither nur noch mit SCHMIED_BEITRAG_SPERRT=1
 *    (server/dreier-modus.ts, `markiereEntwicklerFremd`, `schmiedBeitragGelockert`).
 */

import { entwickler, type EntwicklerId } from "./entwickler.js";

/** Der Schlüssel in den Einstellungen des Kontos. */
export const ENTWICKLER_IM_CHAT_FELD = "entwicklerImChat";

/** Die Quelle eines Beitrags — in `metadata.source` der Zeile und `source` des Chat-Busses. */
export const ENTWICKLER_QUELLE = "entwickler";

/** Die einzige Frageart eines Beitrags: er bittet um ein Ja oder Nein (Knöpfe unter der Blase, frontend/lib/frage-knoepfe.ts). */
export const FRAGE_JA_NEIN = "ja-nein";

export type BeitragFrage = typeof FRAGE_JA_NEIN;

/** Das Feld `frage` eines Beitrags, streng: nur genau dieser eine Wert gilt, alles andere (fehlt, anderer Text, Typ) ist keine Frage. Rein. */
export function frageAus(wert: unknown): BeitragFrage | null {
  return wert === FRAGE_JA_NEIN ? FRAGE_JA_NEIN : null;
}

/**
 * Kartenantwort (20.10.2026, Martin: „Wenn ich auf Ja drücke, muss nicht noch Ja drunter stehen“): Martins echter Tipp auf Ja/Nein einer
 * Karte sendet weiter eine Chat-Zeile an den Schmied, die Zeile wird aber im Chat nicht als eigene Blase gezeigt (die Karte zeigt den
 * Stand). Die Marke gilt NUR für genau das Wort „Ja“/„Nein“ mit Empfänger Schmied — ein getippter Satz oder ein anderer Empfänger bleibt
 * eine normale Blase. Sie ist reine Anzeige: die Zeile bleibt ein Chat-Text, nie ein Ja im Befehlskanal (Zuordnung ≠ Freigabe). Rein.
 */
export function kartenantwortGilt(marke: unknown, an: unknown, text: unknown): boolean {
  if (marke !== true || an !== SCHMIED_ID || typeof text !== "string") return false;
  const t = text.trim();
  return t === "Ja" || t === "Nein";
}

/**
 * Rückfrage des Zimmermanns an Martin (19.10.2026, Befehlskanal P1 — NUR Anzeige, ohne Wirkung): sein Beitrag mit `--ja-nein` trägt eine
 * vom SERVER vergebene Kennung (6 Hex, `metadata.rueckfrage` / Bus-Feld `rueckfrage`). Die Karte (frontend/lib/rueckfrage-karte.ts) bindet
 * Martins Antwort an genau diese Kennung. Kein Beleg, keine Signatur, kein Auftrag; „Chat-Zeilen sind nie ein Ja“ bleibt.
 */
export const RUECKFRAGE_ID_MUSTER = /^[0-9a-f]{6}$/;

/** Eine Rückfrage verfällt nach 24 Stunden. */
export const RUECKFRAGE_HOECHSTALTER_MS = 24 * 60 * 60 * 1000;

/** Die Kennung einer Rückfrage, streng (6 Hex-Zeichen) — sonst `null`. Rein. */
export function rueckfrageIdAus(wert: unknown): string | null {
  return typeof wert === "string" && RUECKFRAGE_ID_MUSTER.test(wert) ? wert : null;
}

/** Die Antwortzeile an Zimmermann — immer mit „kein Beleg“, damit sie nie als Freigabe gelesen wird. Rein. */
export function rueckfrageAntwortText(id: string, antwort: "Ja" | "Nein"): string {
  return `Martin antwortet auf Frage ${id}: ${antwort} (nur Anzeige, kein Beleg)`;
}

/**
 * Das Feld `weiter` eines Beitrags (16.10.2026): „Ergebnis gleich, ich arbeite noch“ — das Auto-Mikro öffnet nach diesem Beitrag
 * NICHT (frontend/lib/schmied-warten.ts). Streng: nur ein ausdrückliches `true` gilt. Mit `frage` (Ja/Nein braucht das Mikro)
 * gilt `frage` vor `weiter`. Ändert nur das Auto-Mikro — nie eine Aktion, nie Martins Ja. Rein.
 */
export function weiterAus(wert: unknown, frage: unknown = null): true | null {
  return wert === true && frageAus(frage) === null ? true : null;
}

/**
 * Das Feld `info` eines Beitrags (16.10.2026, Martin 20:01): „nur zur Info“ — reine Status-/Infomeldung ohne Frage, nichts zu
 * beantworten. Nach dem Vorlesen öffnet das Auto-Mikro NICHT und wartet auch nicht auf den Schmied (frontend/lib/schmied-warten.ts).
 * Streng: nur ein ausdrückliches `true` gilt. Eine Frage (`frage`) und `weiter` („ich arbeite noch“) gehen vor — damit bleibt nie
 * eine Frage ohne Mikro. Ändert nur das Auto-Mikro — nie eine Aktion, nie Martins Ja. Rein.
 */
export function infoAus(wert: unknown, frage: unknown = null, weiter: unknown = null): true | null {
  return wert === true && frageAus(frage) === null && weiter !== true ? true : null;
}

/** Längster Beitrag in Zeichen. */
export const BEITRAG_HOECHSTENS = 2000;

/** Höchstens so viele Beiträge je Stunde (gleitend). */
export const BEITRAEGE_JE_STUNDE = 40;

/** Nur dieses Konto (Betreiber, SUPERUSER_ID in server/werkzeug-bereiche.ts). */
const KONTO = 1;

/** Gilt der Schalter für dieses Konto überhaupt (und steht er darum in den Einstellungen)? Rein. */
export function entwicklerImChatFuerKonto(userId: number | null | undefined): boolean {
  return userId === KONTO;
}

/** Ist der Dreier-Modus an? Nur Konto 1 und nur bei ausdrücklichem `true`. Rein. */
export function entwicklerImChatAn(wert: unknown, userId: number | null | undefined): boolean {
  return entwicklerImChatFuerKonto(userId) && wert === true;
}

/** Der Schlüssel des Hauptschalters „Entwickler“ (18.10.2026): schaltet alle drei Entwickler zugleich, ohne ihre Einzelschalter zu verändern. */
export const ENTWICKLER_GESAMT_FELD = "entwicklerGesamt";

/**
 * Ist der Hauptschalter „Entwickler“ an? (18.10.2026) Vorgabe AN: fehlt das Feld (bestehende Konten), bleibt alles wie bisher.
 * Aus ist alles andere als `true` oder „fehlt“ — ein ausdrückliches `false`, aber auch jeder fremde Wert (Fehlkonfiguration öffnet nie).
 * Nur Konto 1. Rein.
 */
export function entwicklerGesamtAn(wert: unknown, userId: number | null | undefined): boolean {
  return entwicklerImChatFuerKonto(userId) && (wert === undefined || wert === true);
}

/**
 * DIE EINE STELLE für „darf überhaupt jemand vom Team in den Chat“: Hauptschalter „Entwickler“ UND „Entwickler im Chat“.
 * Jede Prüfung am Server (Tür, Kopfzeile, Marker, Adressat, Zug-Hinweis) geht hierüber; ein Lese-Fehler (`einstellungen` ist `null`)
 * ergibt aus, weil „Entwickler im Chat“ ein ausdrückliches `true` braucht. Rein.
 */
export function entwicklerHauptAn(einstellungen: Readonly<Record<string, unknown>> | null | undefined, userId: number | null | undefined): boolean {
  return entwicklerImChatAn(einstellungen?.[ENTWICKLER_IM_CHAT_FELD], userId) && entwicklerGesamtAn(einstellungen?.[ENTWICKLER_GESAMT_FELD], userId);
}

/**
 * Darf dieser Entwickler nach den Einstellungen des Kontos in den Chat? (16.10.2026, shared/entwickler.ts) Immer erst der Hauptschalter
 * (`entwicklerHauptAn`: „Entwickler“ und „Entwickler im Chat“); der Schmied braucht nichts weiter, jeder andere zusätzlich seinen eigenen Kontoschalter (`kontoFeld`, nur ein
 * ausdrückliches `true`). Rein.
 */
export function entwicklerKontoAn(id: EntwicklerId, einstellungen: Readonly<Record<string, unknown>> | null | undefined, userId: number | null | undefined): boolean {
  if (!entwicklerHauptAn(einstellungen, userId)) return false;
  const feld = entwickler(id).kontoFeld;
  return feld === null || einstellungen?.[feld] === true;
}

/** Ist diese Zeile (chat_messages oder Bus) ein Beitrag des Entwicklers? Nur `system` mit der Quelle zählt. Rein. */
export function istEntwicklerZeile(role: unknown, metadataOderQuelle: unknown): boolean {
  if (role !== "system") return false;
  if (metadataOderQuelle === ENTWICKLER_QUELLE) return true;
  return (
    typeof metadataOderQuelle === "object" &&
    metadataOderQuelle !== null &&
    (metadataOderQuelle as { source?: unknown }).source === ENTWICKLER_QUELLE
  );
}

/** Die Kennung des Schmieds — des einzigen Entwicklers, dessen Beiträge die Fremdtext-Sperre nicht auslösen (server/dreier-modus.ts). */
export const SCHMIED_ID = "schmied";

/** Die Kennung des Zimmermanns (shared/entwickler.ts) — für die Felder, die auch für ihn gelten (`weiter`/`info`, server/dreier-routen.ts). */
export const ZIMMERMANN_ID = "zimmermann";

/** Die Kennung des Hüters (17.10.2026, Server 7, shared/entwickler.ts) — dieselben Felder wie beim Zimmermann (`weiter`/`info`). */
export const HUETER_ID = "hueter";

/**
 * Wer hat diesen Beitrag geschrieben? (16.10.2026, Prüfung der Lockerung) `metadata.entwickler` — fehlt das Feld, war es der Schmied
 * (alle Beiträge vor der Einführung eines zweiten Entwicklers). Ein Name aus Kleinbuchstaben, Ziffern, `-`/`_` (höchstens 40) wird
 * gefaltet zurückgegeben; alles andere (leer, kein Text, andere Zeichen) ist `null` = unbekannt und gilt NIE als Schmied. Rein.
 */
export function entwicklerIdAus(metadata: unknown): string | null {
  if (metadata === ENTWICKLER_QUELLE) return SCHMIED_ID; // Bus-Form: nur die Quelle
  if (typeof metadata !== "object" || metadata === null) return SCHMIED_ID;
  if (!("entwickler" in metadata) || (metadata as { entwickler?: unknown }).entwickler === undefined) return SCHMIED_ID;
  const wert = (metadata as { entwickler?: unknown }).entwickler;
  if (typeof wert !== "string") return null;
  const id = wert.trim().toLowerCase();
  return /^[a-z0-9_-]{1,40}$/.test(id) ? id : null;
}
